import { unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";
import { productInfo, productInfos, warehouses } from "../lookups.ts";
import { rest } from "../rest.ts";
import type {
  ActiveOp,
  BoardCard,
  BoardColumn,
  KitResult,
  ManufacturingOrder,
  MarkRequest,
  OrderState,
  RouteSummary,
  UnitCard,
  UnitPosition,
  UnitState,
} from "./types.ts";

/*
 * Производство на REST из contracts/openapi.json (модуль manufacturing,
 * PR #38). Названия изделий и компонентов из справочника, склады по id.
 * Маршрута в контракте нет: позиция изделия, сводка маршрута, «Випустити
 * готові», «Мої операції» ходят в /api/v1/route по модели
 * Android-клиента и заработают, когда поток B даст этот REST.
 */
type S = components["schemas"];

const MFG = "/api/v1/manufacturing";
const ROUTE = "/api/v1/route";
const enc = encodeURIComponent;
const id = (v: string) => ({ params: { path: { id: v } } });

const NO_POSITION: UnitPosition = { step: null, routeDone: false, quarantined: false, quarantineReason: null, stuck: false, idleHours: 0, packedWithOpenRoute: false };

/** Поля сверх контракта, если сервер их присылает. */
type Extra = { kitRequest?: string | null; notes?: string[]; route?: RouteSummary | null };

async function order(o: S["ManufacturingOrder"]): Promise<ManufacturingOrder> {
  const [product, infos, list] = await Promise.all([productInfo(o.productId), productInfos(o.kit.map((k) => k.productId)), warehouses()]);
  const w = list.find((x) => x.id === o.warehouseId);
  const extra = o as S["ManufacturingOrder"] & Extra;
  return {
    ...o,
    state: o.state as OrderState,
    version: o.version ?? null,
    batch: o.batch ?? null,
    salesOrder: o.salesOrder ?? null,
    completedOn: o.completedOn ?? null,
    productName: product.name.replace(/ \([^)]*\)$/, ""),
    warehouseName: w?.name ?? o.warehouseId,
    prototype: w?.code === "RND",
    kitRequest: extra.kitRequest ?? null,
    notes: extra.notes ?? [],
    kit: o.kit.map((k) => {
      const p = infos.get(k.productId);
      const packing = (k as typeof k & { packing?: boolean }).packing ?? false;
      return { ...k, productSku: p?.sku ?? k.productId, productName: p?.name ?? k.productId, uom: p?.uom ?? "шт", packing };
    }),
    units: o.units.map((u) => ({
      ...u,
      state: u.state as UnitState,
      assembledOn: u.assembledOn ?? null,
      position: (u as typeof u & { position?: UnitPosition }).position ?? NO_POSITION,
    })),
  };
}

const orderOf = async (p: Promise<{ data?: S["ManufacturingOrder"]; error?: unknown; response: Response }>) => order(await unwrap(p));

export const productionApi = {
  /** Доска: карточка контракта плюс заказ целиком ради изделия, комплекта и серийников. */
  board: async (warehouseId = "SER"): Promise<BoardCard[]> => {
    const cards = await unwrap(api.GET("/api/v1/manufacturing/board", { params: { query: { warehouseId } } }));
    return Promise.all(
      cards.map(async (c) => {
        const o = await productionApi.order(c.orderId);
        const kit = o.kit.filter((k) => !k.packing);
        return {
          ...c,
          column: c.column as BoardColumn,
          productName: o.productName,
          version: o.version,
          qty: o.qty,
          start: o.start,
          origin: o.salesOrder,
          kitTotal: kit.length,
          kitShort: kit.filter((k) => k.short).length,
          kitRequest: o.kitRequest,
          firstSerial: o.units[0]?.serial ?? null,
          lastSerial: o.units.at(-1)?.serial ?? null,
          route: (c as typeof c & Extra).route ?? null,
        };
      }),
    );
  },
  order: (v: string) => orderOf(api.GET("/api/v1/manufacturing/orders/{id}", id(v))),
  kit: (v: string): Promise<KitResult> =>
    unwrap(api.POST("/api/v1/manufacturing/orders/{id}/kit", id(v))).then((r) => ({ kitRequest: r.kitRequest ?? null, packingRequest: r.packingRequest ?? null })),
  serials: (v: string) => unwrap(api.POST("/api/v1/manufacturing/orders/{id}/serials", { ...id(v), body: { printLabels: true } })),
  addUnits: (v: string, count: number) => orderOf(api.POST("/api/v1/manufacturing/orders/{id}/units", { ...id(v), body: { count } })),
  cancel: (v: string) => orderOf(api.POST("/api/v1/manufacturing/orders/{id}/cancel", id(v))),
  /** «Випустити готові»: в контракте нет, на сервере RouteService.packReady. */
  packReady: (v: string) => rest<string[]>("POST", `${MFG}/orders/${enc(v)}/pack-ready`),
  assemble: (serials: string[]) => unwrap(api.POST("/api/v1/manufacturing/assemblies", { body: { serials } })).then(() => null),
  pack: (serial: string, routeClosed: boolean) =>
    unwrap(api.POST("/api/v1/manufacturing/units/{serial}/pack", { params: { path: { serial } }, body: { routeClosed } })).then(() => null),
  writeOff: (serial: string, reason: string) =>
    unwrap(api.POST("/api/v1/manufacturing/units/{serial}/write-off", { params: { path: { serial } }, body: { reason } })).then(() => null),

  card: (serial: string) => rest<UnitCard>("GET", `${ROUTE}/units/${enc(serial)}`),
  mark: (body: MarkRequest) => rest<UnitCard>("POST", `${ROUTE}/marks`, body),
  myActive: () => rest<ActiveOp | null>("GET", `${ROUTE}/my/active`),
};
